"use client";

import {
  frozen,
  isRootStore,
  model,
  objectMap,
  registerRootStore,
  unregisterRootStore,
} from "mobx-keystone";
import {
  getRouterConfig,
  getStoreConfig,
  setRouterConfig,
  setStoreConfig,
} from "../config";
import { browserRouterAdapter } from "../router/adapters/browser";
import { TenantStore, TTenant } from "./lib/models/tenant-store";
import RootStore from "./root";
import { AuthStore } from "./stores/auth";
import type { RouterAdapter } from "../router/adapter";
import type { StoreConfig } from "../storage/config";
import type { RootCreationData } from "./root";

@model("concreteTenant")
class ConcreteTenant extends TTenant {}

declare global {
  interface Window {
    _DEBUG_RootStore: () => RootStore;
  }
}

interface RouterOpts {
  routerAdapter?: RouterAdapter;
}

const isServer = typeof window === "undefined";
let _cachedRootStore: RootStore | null = null;

function buildRootStore(initialData: RootCreationData) {
  const activeTenantModel = new ConcreteTenant({
    refId: initialData.tenant.refId,
    supportedNudgeChannels: initialData.tenant.supportedNudgeChannels,
  });

  return new RootStore({
    auth: new AuthStore({
      ...initialData.auth,
      session: frozen(JSON.parse(JSON.stringify(initialData.auth.session))),
      lastSync: new Date(),
    }),
    x: null,
    tenant: new TenantStore({
      globalMode: false,
      activeTenantId: initialData.tenant.refId,
      tenants: objectMap([[initialData.tenant.refId, activeTenantModel]]),
    }),
    lastSync: new Date(),
  });
}

// TODO: have RootStoreProvider take in an env config and have it change package env/ config here
export function createRootStore(
  initialData: RootCreationData,
  storageOpts: StoreConfig = getStoreConfig(),
  routerOpts: RouterOpts = {
    routerAdapter: isServer ? undefined : browserRouterAdapter(),
  },
) {
  // Server: construct a fresh RootStore each render — no registration, no caching,
  // no browser-specific setup. onInit skips the storage adapter call when
  // typeof window === "undefined".
  if (isServer) {
    return buildRootStore(initialData);
  }

  if (_cachedRootStore) {
    return _cachedRootStore;
  }

  if (storageOpts !== getStoreConfig()) {
    setStoreConfig(storageOpts);
  }

  if (
    routerOpts.routerAdapter &&
    routerOpts.routerAdapter !== getRouterConfig().routerAdapter
  ) {
    setRouterConfig({
      routerAdapter: routerOpts.routerAdapter,
    });
  }

  const root = buildRootStore(initialData);

  if (isRootStore(root)) {
    console.warn("RootStore already registered. Reregistering...");
    unregisterRootStore(root);
  }

  const rootStore = registerRootStore(root);
  _cachedRootStore = rootStore;

  if (typeof window !== "undefined") {
    window._DEBUG_RootStore = () => rootStore;
  }

  return rootStore;
}
